import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FaEye, FaEyeSlash, FaSortDown, FaSortUp } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { Report } from 'notiflix/build/notiflix-report-aio'
import api from '../../../helpers/apiRequester'
import { formatDate } from '../omnibusShared/formatting'
import styles from './index.module.scss'

/**
 * Admin > Omnibus Batch Visibility.
 *
 * Hide or show whole funding batches across the omnibus system. The backend
 * gates both endpoints to admins + Sentinel org users; anyone else gets a 403
 * from the list call and is bounced back to the dashboard. The toggle state
 * is global and persistent — every visitor sees the omnibuses with or
 * without a hidden batch's claims.
 */

interface BatchVisibilityRow {
    batch_group: string
    nera_id: string | null
    funding_claims: number
    funding_clients: number
    omnibus_claims: number
    batch_status: string
    visible: boolean
    updated_by: string | null
    updated_at: string | null
}

type SortField =
    | 'batch_group'
    | 'nera_id'
    | 'funding_claims'
    | 'funding_clients'
    | 'omnibus_claims'
    | 'batch_status'
    | 'visible'
    | 'updated_at'

/** Funding progression order, furthest stage first-class for sorting. */
const STATUS_RANK: Record<string, number> = {
    'Not started': 0,
    'ATE requested': 1,
    'ATE collected': 2,
    'Submission sheet submitted': 3,
    'API transfer completed': 4,
}

/**
 * NERA IDs are "ur<number>" (with the odd stray space, e.g. "ur r1"), so a
 * plain string sort puts ur12 before ur2. Compare by the number instead;
 * batches without a NERA ID sort last.
 */
const neraRank = (neraId: string | null) => {
    const digits = String(neraId || '').replace(/\D/g, '')
    return digits ? Number(digits) : Number.POSITIVE_INFINITY
}

const compareRows = (a: BatchVisibilityRow, b: BatchVisibilityRow, field: SortField): number => {
    switch (field) {
        case 'nera_id':
            return neraRank(a.nera_id) - neraRank(b.nera_id) || a.batch_group.localeCompare(b.batch_group)
        case 'batch_group':
            return a.batch_group.localeCompare(b.batch_group)
        case 'funding_claims':
        case 'funding_clients':
        case 'omnibus_claims':
            return a[field] - b[field]
        case 'batch_status':
            return (STATUS_RANK[a.batch_status] ?? 0) - (STATUS_RANK[b.batch_status] ?? 0)
        case 'visible':
            return Number(a.visible) - Number(b.visible)
        case 'updated_at':
            return String(a.updated_at || '').localeCompare(String(b.updated_at || ''))
    }
}

export const BatchVisibilityView = () => {
    const navigate = useNavigate()
    const [batches, setBatches] = useState<BatchVisibilityRow[]>([])
    const [loading, setLoading] = useState(true)
    const [busyBatch, setBusyBatch] = useState<string | null>(null)
    const [sortBy, setSortBy] = useState<SortField>('nera_id')
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

    const setSort = (field: SortField) => {
        if (field === sortBy) {
            setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortBy(field)
            setSortDir('asc')
        }
    }

    const sortedBatches = useMemo(() => {
        const sorted = [...batches].sort((a, b) => compareRows(a, b, sortBy))
        return sortDir === 'desc' ? sorted.reverse() : sorted
    }, [batches, sortBy, sortDir])

    const fetchBatches = useCallback(
        async (opts?: { silent?: boolean }) => {
            if (!opts?.silent) setLoading(true)
            try {
                const response = await api.post('omnibus-batch-visibility-list', {})
                if (response.data?.success) setBatches(response.data.batches || [])
            } catch (err: any) {
                if (err?.response?.status === 403) {
                    Report.failure(
                        'Access Denied',
                        'Batch visibility is managed by admins only. Please contact your administrator if you need access.',
                        'Return to Dashboard',
                        () => navigate('/home'),
                    )
                } else {
                    Report.failure('Error', 'Could not load funding batches.', 'Okay')
                }
            } finally {
                setLoading(false)
            }
        },
        [navigate],
    )

    useEffect(() => {
        fetchBatches()
    }, [fetchBatches])

    const toggle = async (row: BatchVisibilityRow) => {
        setBusyBatch(row.batch_group)
        try {
            const response = await api.post('omnibus-batch-visibility-set', {
                batch_group: row.batch_group,
                visible: !row.visible,
            })
            if (response.data?.success) {
                await fetchBatches({ silent: true })
            } else {
                Report.failure('Error', response.data?.message || 'Could not update batch visibility.', 'Okay')
            }
        } catch (err: any) {
            Report.failure(
                'Error',
                err?.response?.data?.message || 'Could not update batch visibility.',
                'Okay',
            )
        } finally {
            setBusyBatch(null)
        }
    }

    const hiddenCount = batches.filter((b) => !b.visible).length

    return (
        <div className={styles.wrap}>
            <p className={styles.summaryLine}>
                {loading
                    ? 'Loading funding batches...'
                    : `${batches.length.toLocaleString('en-GB')} funding batch(es)${
                          hiddenCount > 0
                              ? ` — ${hiddenCount} currently hidden from the omnibus system`
                              : ' — all visible'
                      }.`}
            </p>

            <table className={styles.table}>
                <thead>
                    <tr>
                        {(
                            [
                                ['batch_group', 'Batch'],
                                ['nera_id', 'NERA ID'],
                                ['funding_claims', 'Claims in funding'],
                                ['funding_clients', 'Clients in batch'],
                                ['omnibus_claims', 'Claims in omnibuses'],
                                ['batch_status', 'Funding status'],
                                ['visible', 'State'],
                                ['updated_at', 'Last changed'],
                            ] as Array<[SortField, string]>
                        ).map(([field, label]) => (
                            <th key={field} aria-sort={sortBy === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}>
                                <button type="button" className={styles.sortHeader} onClick={() => setSort(field)}>
                                    {label}
                                    {sortBy === field &&
                                        (sortDir === 'asc' ? <FaSortUp aria-hidden /> : <FaSortDown aria-hidden />)}
                                </button>
                            </th>
                        ))}
                        <th aria-label="Toggle" />
                    </tr>
                </thead>
                <tbody>
                    {sortedBatches.map((row) => (
                        <tr key={row.batch_group} className={row.visible ? '' : styles.hiddenRow}>
                            <td className={styles.batchName}>{row.batch_group}</td>
                            <td className={styles.batchName}>{row.nera_id || '—'}</td>
                            <td>{row.funding_claims.toLocaleString('en-GB')}</td>
                            <td>{row.funding_clients.toLocaleString('en-GB')}</td>
                            <td>{row.omnibus_claims.toLocaleString('en-GB')}</td>
                            <td>
                                <span
                                    className={`${styles.statusPill} ${
                                        row.batch_status === 'Not started' ? styles.statusNotStarted : ''
                                    }`}
                                >
                                    {row.batch_status}
                                </span>
                            </td>
                            <td>
                                <span className={row.visible ? styles.visiblePill : styles.hiddenPill}>
                                    {row.visible ? 'Visible' : 'Hidden'}
                                </span>
                            </td>
                            <td className={styles.changedBy}>
                                {row.updated_at ? formatDate(row.updated_at) : '—'}
                            </td>
                            <td>
                                <button
                                    type="button"
                                    className={styles.toggleBtn}
                                    disabled={busyBatch === row.batch_group}
                                    onClick={() => toggle(row)}
                                    title={
                                        row.visible
                                            ? 'Hide this batch from all omnibus views and new schedules'
                                            : 'Show this batch in omnibus views again'
                                    }
                                >
                                    {row.visible ? <FaEyeSlash /> : <FaEye />}
                                    {row.visible ? 'Hide' : 'Show'}
                                </button>
                            </td>
                        </tr>
                    ))}
                    {!loading && batches.length === 0 && (
                        <tr>
                            <td colSpan={9} className={styles.empty}>No funding batches found.</td>
                        </tr>
                    )}
                </tbody>
            </table>

            <p className={styles.note}>
                Hiding a batch removes its claims from omnibus counts, claim lists and new schedule
                builds for every user. Claims already on a sent schedule are a historical record and
                are not altered. Nothing is deleted — toggling a batch back on restores its claims
                immediately.
            </p>
        </div>
    )
}
